import React, { useEffect, useState, useCallback } from 'react';
import Select from 'src/components/ui/form/select';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import TGButton from 'src/components/ui/TGButton';
import { FlexRow } from 'src/utils/flexLayouts';
import AddActionInComponent from 'src/pages/DashboardPage/mytasks/addAction/inComponent';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectLastAddedId, selectLastDocId, selectModelData } from '../../selectors/selectors';
import { addData, fetchData, setLastAddedId, deleteData } from '../../actions/actions';
import { PlanActionsContext } from '../../types/types';

interface AddActionGopProps {
  toggleDoing: (value: boolean) => void;
  isDisabled?: boolean;
  isAutoSave?: boolean;
  firstActionId?: number;
  onActionAdded?: () => void;
  context: PlanActionsContext;
}

export default function AddActionGop({ toggleDoing, isDisabled, isAutoSave, firstActionId, onActionAdded, context }: AddActionGopProps) {
  const dispatch = useAppDispatch();
  const dataDirection = useAppSelector((state: any) => selectModelData(state, 'gop-directions'));
  const dataBlock = useAppSelector((state: any) => selectModelData(state, 'gop-blocks'));
  const dataDemands = useAppSelector((state: any) => selectModelData(state, 'gop-requirements'));
  const lastAddedIdforDelete = useAppSelector((state) => selectLastDocId(state, 'gop-actions'));
  const lastAddedId = useAppSelector((state) => selectLastAddedId(state, 'gop-actions'));

  const [isInitialSaveComplete, setIsInitialSaveComplete] = useState(false);
  const [isAutoFillComplete, setIsAutoFillComplete] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [savedActionId, setSavedActionId] = useState<number | null>(null);

  const { selectedGroup, selectedFactory, selectedGop, currentQuarter, currentYear, currentBlock, currentDirection, currentRequirement, isPrefillMode = false } = context;

  const isPrefilledForm = isPrefillMode;

  //! Направления по форме гоп
  useEffect(() => {
    const selectedGopIds = context.selectedGop?.id;
    if (selectedGopIds) {
      dispatch(fetchData('gop-directions', { gop: selectedGopIds }));
    }
  }, [dispatch, context.selectedGop?.id]);

  const cancel = useCallback(() => {
    if (isAutoSave) {
      toggleDoing(false);
      return;
    }
    dispatch(setLastAddedId('gop-actions', null));

    if (isPrefilledForm && lastAddedIdforDelete) {
      dispatch(deleteData('gop-actions', lastAddedIdforDelete));
    }
    setTimeout(() => {
      if (onActionAdded) {
        onActionAdded();
      }
      toggleDoing(false);
    }, 500);
    toggleDoing(false);
  }, [isPrefilledForm, lastAddedIdforDelete, dispatch, onActionAdded, toggleDoing, isAutoSave]);

  const defaultValues = {
    direction: null,
    block: null,
    demand: null,
  };

  const methods = useForm<any>({
    defaultValues: defaultValues,
  });

  const { handleSubmit, control, watch } = methods;
  const selectedDirection = watch('direction');
  const selectedBlock = watch('block');

  const isDirectionFromContext = !!(currentDirection && selectedDirection?.id === currentDirection.id);
  const isBlockFromContext = !!(currentBlock && selectedBlock?.id === currentBlock.id);
  const isRequirementFromContext = !!(currentRequirement && watch('demand')?.id === currentRequirement.id);

  //! Предзаполненность формы из контекста
  useEffect(() => {
    if (!isPrefilledForm) return;

    let fieldsSet = 0;
    const totalFields = (currentDirection ? 1 : 0) + (currentBlock ? 1 : 0) + (currentRequirement ? 1 : 0);

    if (currentDirection && !selectedDirection) {
      methods.setValue('direction', {
        id: currentDirection.id,
        name: currentDirection.Title,
      });
      fieldsSet++;
    }
    if (currentBlock && !selectedBlock) {
      methods.setValue('block', {
        id: currentBlock.id,
        name: currentBlock.title,
      });
      fieldsSet++;
    }
    if (currentRequirement) {
      const currentDemand = watch('demand');
      if (!currentDemand) {
        methods.setValue('demand', {
          id: currentRequirement.id,
          name: currentRequirement.title,
          isFocal: currentRequirement.isFocal,
        });
        fieldsSet++;
      }
    }
    if (fieldsSet === totalFields && totalFields > 0) {
      setIsAutoFillComplete(true);
    }
  }, [isPrefilledForm, currentDirection, currentBlock, currentRequirement, selectedDirection, selectedBlock, methods]);

  //! При изменении направления получаю блоки
  useEffect(() => {
    if (selectedDirection?.id) {
      dispatch(
        fetchData(
          'gop-blocks',
          {
            direction: selectedDirection.id,
          },
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
    }

    if (!isPrefilledForm) {
      methods.setValue('block', null);
      methods.setValue('demand', null);
    }
  }, [selectedDirection, dispatch, isPrefilledForm, methods]);

  //! При изменении блока получаю требования
  useEffect(() => {
    if (selectedBlock?.id) {
      dispatch(
        fetchData(
          'gop-requirements',
          {
            block: selectedBlock.id,
          },
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
    }

    if (!isPrefilledForm) {
      methods.setValue('demand', null);
    }
  }, [selectedBlock, dispatch, isPrefilledForm, methods]);

  useEffect(() => {
    if (lastAddedId && lastAddedId > 0) {
      setIsDialogOpen(true);
    }
  }, [lastAddedId]);

  //! Автосейв при открытии предзаполненной формы
  useEffect(() => {
    if (!isPrefilledForm || !isAutoFillComplete || isAutoSave) return;

    const timer = setTimeout(() => {
      const currentValues = methods.getValues();
      const allFieldsFilled = currentValues.direction && currentValues.block && currentValues.demand;

      if (!isInitialSaveComplete && allFieldsFilled) {
        const initialData = {
          directionID: currentValues.direction?.id,
          directionTitle: currentValues.direction?.name,
          blockID: currentValues.block?.id,
          blockTitle: currentValues.block?.name,
          demandID: currentValues.demand?.id,
          demandTitle: currentValues.demand?.name,
          IsFocal: currentValues.demand?.isFocal,
          group: selectedGroup?.id,
          factory: selectedFactory.id,
          gop: selectedGop.id,
          quarter: currentQuarter,
          year: currentYear,
        };

        dispatch(addData('gop-actions', initialData));
        setIsInitialSaveComplete(true);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isPrefilledForm, isAutoFillComplete, isInitialSaveComplete, dispatch, methods, selectedGroup, selectedFactory, selectedGop, currentQuarter, currentYear, isAutoSave]);

  const onSubmit = (data: any) => {
    const requestBody = {
      directionID: data.direction?.id,
      directionTitle: data.direction?.name,
      blockID: data.block?.id,
      blockTitle: data.block?.name,
      demandID: data.demand?.id,
      demandTitle: data.demand?.name,
      IsFocal: data.demand?.isFocal,
      group: selectedGroup?.id,
      factory: selectedFactory.id,
      gop: selectedGop.id,
      quarter: currentQuarter,
      year: currentYear,
    };

    dispatch(addData('gop-actions', requestBody));
  };

  useEffect(() => {
    if (lastAddedId && !savedActionId) {
      setSavedActionId(lastAddedId);
      setIsDialogOpen(true);
    }
  }, [lastAddedId, savedActionId]);

  const actionIdForComponent = firstActionId || savedActionId;

  const closeForm = useCallback(() => {
    toggleDoing(false);
    if (onActionAdded) {
      onActionAdded();
    }
    dispatch(setLastAddedId('gop-actions', null));
  }, [toggleDoing, onActionAdded]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='space-y-5'>
          <section className='grid grid-cols-2 gap-5'>
            <div>
              <Controller
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    dataField={{
                      placeholder: 'выберите направление',
                      type: 'select',
                      require: true,
                      title: 'Направление',
                      width: 'w-full',
                      key: 'direction',
                      single: true,
                      disabled: isDisabled || isDirectionFromContext,
                      options:
                        dataDirection?.map((item) => ({
                          id: item.id,
                          name: item.Title,
                        })) || [],
                    }}
                    errors={{ direction: !!error }}
                  />
                )}
                name='direction'
                control={control}
              />
            </div>
            {/* Блок */}
            <div>
              <Controller
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    dataField={{
                      placeholder: !selectedDirection ? 'Сначала выберите направление' : 'Выберите блок',
                      type: 'select',
                      require: true,
                      title: 'Блок',
                      width: 'w-full',
                      key: 'block',
                      single: true,
                      disabled: isDisabled || !selectedDirection || isBlockFromContext,
                      options:
                        dataBlock?.map((item) => ({
                          id: item.id,
                          name: item.Title || item.title || '',
                        })) || [],
                    }}
                    errors={{ block: !!error }}
                  />
                )}
                name='block'
                control={control}
              />
            </div>
          </section>
          {/* Требование */}
          <div>
            <Controller
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  value={value}
                  onChange={onChange}
                  dataField={{
                    placeholder: !selectedBlock ? 'Сначала выберите блок' : 'Выберите требование',
                    type: 'select',
                    require: true,
                    title: 'Требование',
                    width: 'w-full',
                    key: 'demand',
                    single: true,
                    disabled: isDisabled || !selectedBlock || isRequirementFromContext,
                    options:
                      dataDemands?.map((item) => ({
                        id: item.id,
                        name: item.Title || item.title || '',
                        isFocal: item.isFocal || false,
                      })) || [],
                  }}
                  errors={{ demand: !!error }}
                />
              )}
              name='demand'
              control={control}
            />
          </div>
        </div>
      </form>
      <div className='mt-4'>
        <h3 className='text-[#637381] font-medium mb-4'>Действия</h3>

        <AddActionInComponent
          key={`action-component-${actionIdForComponent}`}
          typeModule={'GOPСтандарты'}
          idModule={actionIdForComponent}
          CustomStyles={'bg-white p-5'}
          customTypes={actionIdForComponent > 0 ? `add_action_in_component_${actionIdForComponent}` : undefined}
        />
      </div>
      <FlexRow className='justify-between mt-10'>
        <TGButton type='button' onClick={cancel}>
          Отмена
        </TGButton>
        {isDialogOpen || lastAddedId || firstActionId ? (
          <TGButton type='submit' color='bluedark' onClick={closeForm}>
            Сохранить
          </TGButton>
        ) : (
          <TGButton type='submit' color='bluedark' onClick={handleSubmit(onSubmit)}>
            Сохранить
          </TGButton>
        )}
      </FlexRow>
    </FormProvider>
  );
}
